import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import { getTenant } from "../../utils/tenantManager";

import {
  createProductApi,
  deleteProductApi,
  getProductByIdApi,
  getProductsApi,
  updateProductApi,
} from "../../services/website/productService";

export const useProductViewModel = () => {
  const tenantId = getTenant();

  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(Number(searchParams.get("page") ?? "1"));

  const [search, setSearch] = useState(searchParams.get("search") ?? "");

  // ================= LOAD =================

  const loadProducts = async () => {
    try {
      setLoading(true);

      const res = await getProductsApi(page, 10, tenantId!, search);
      setProducts(res.data || []);
      setTotalCount(res.totalCount || 0);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [page, search]);

  // ================= CREATE =================

  const createProduct = async (data: FormData) => {
    try {
      const res = await createProductApi(data, tenantId!);

      if (res?.success) {
        toast.success("Product created");

        loadProducts();

        return true;
      }

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Create failed");

      return false;
    }
  };

  // ================= GET BY ID =================

  const getProductById = async (id: string) => {
    try {
      const res = await getProductByIdApi(id, tenantId!);

      return res?.data;
    } catch {
      toast.error("Failed to load product");
    }
  };

  // ================= UPDATE =================

  const updateProduct = async (data: FormData) => {
    try {
      const res = await updateProductApi(data, tenantId!);

      if (res?.success) {
        toast.success("Product updated");

        loadProducts();

        return true;
      }

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");

      return false;
    }
  };

  // ================= DELETE =================
  const deleteProduct = async (id: string) => {
    try {
      const res = await deleteProductApi(id, tenantId!);

      if (res?.success) {
        toast.success("Product deleted");
        return true;
      }

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed");
      return false;
    }
  };

  return {
    // CRUD
    createProduct,
    updateProduct,
    deleteProduct,
    getProductById,

    // DATA
    products,
    loading,
    totalCount,

    // PAGINATION
    page,
    setPage,

    // SEARCH
    search,
    setSearch,

    // LOAD
    loadProducts,
  };
};
